/* ============================================================================
 * notificationsData.js — feed for the in-app notification bell.
 *
 * Aggregates notable contractor-side events from existing data (no new table):
 *   - message   : unread client → contractor portal messages (like a text)
 *   - signature : a client signed an estimate (from the alerts feed)
 *   - lead      : a new lead was captured
 *
 * "Unread" logic:
 *   - Messages are authoritative via their own per-message `read` flag (cleared
 *     when the contractor opens the thread in the Client Portal inbox), so a
 *     message stays unread until actually read — that's the "like a text" feel.
 *   - Signatures / leads use a single `seen_at` high-water mark (localStorage):
 *     anything newer than seen_at is unread. Opening the bell advances seen_at.
 *   - First run initializes seen_at to now so the badge starts from "caught up"
 *     instead of lighting up for every historical lead.
 * ========================================================================== */

import { supabase } from '@/lib/supabaseClient';
import {
  fetchPortalMessages,
  fetchPortalConfig,
  fetchPortalAlerts,
  flattenInbox,
} from '@/lib/clientPortalData';

const SEEN_KEY = 'ko_notifications_seen_at';

export const getSeenAt = () => {
  try {
    return localStorage.getItem(SEEN_KEY) || '';
  } catch {
    return '';
  }
};

export const markAllSeen = () => {
  const t = new Date().toISOString();
  try {
    localStorage.setItem(SEEN_KEY, t);
  } catch {
    /* ignore */
  }
  return t;
};

/** Compact relative time: "just now", "5m ago", "3h ago", "2d ago", "Mar 5". */
export const relativeTime = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const s = Math.floor((Date.now() - then) / 1000);
  if (s < 45) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '';
  }
};

const fetchRecentLeads = async () => {
  try {
    const { data, error } = await supabase
      .from('clients')
      .select('id,name,type,source,created_at')
      .eq('type', 'lead')
      .order('created_at', { ascending: false })
      .limit(15);
    if (error) return [];
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
};

/**
 * Gather the notification feed + unread count.
 * Returns { items: [{ id, kind, title, body, at, unread, count?, link }], unreadCount }.
 */
export const gatherNotifications = async () => {
  let seen = getSeenAt();
  if (!seen) seen = markAllSeen(); // first run: start caught-up

  let messages = {};
  let portals = {};
  let alerts = [];
  let leads = [];
  try {
    [messages, portals, alerts, leads] = await Promise.all([
      fetchPortalMessages(),
      fetchPortalConfig(),
      fetchPortalAlerts(),
      fetchRecentLeads(),
    ]);
  } catch {
    /* degrade to whatever resolved; empty is fine */
  }

  const items = [];

  // 1) Unread client message threads (authoritative via read flags).
  const inbox = flattenInbox(messages || {}, portals || {});
  for (const row of inbox) {
    if (row.unread > 0) {
      items.push({
        id: `msg-${row.client_id}`,
        kind: 'message',
        title: row.client_name || 'Client',
        body: row.last_body || 'New message',
        at: row.last_at,
        unread: true,
        count: row.unread,
        link: '/dashboard/client-portal',
      });
    }
  }

  // 2) Estimate signatures (from the alerts feed).
  for (const a of Array.isArray(alerts) ? alerts : []) {
    if (a?.kind === 'signature') {
      items.push({
        id: `sig-${a.id}`,
        kind: 'signature',
        title: `${a.signed_name || 'Client'} signed`,
        body: a.estimate_title || 'Estimate',
        at: a.created_at,
        unread: seen ? String(a.created_at || '') > seen : true,
        link: '/dashboard/estimates',
      });
    }
  }

  // 3) New leads.
  for (const c of leads) {
    items.push({
      id: `lead-${c.id}`,
      kind: 'lead',
      title: `New lead: ${c.name || 'Unnamed'}`,
      body: c.source ? `Heard via ${c.source}` : '',
      at: c.created_at,
      unread: seen ? String(c.created_at || '') > seen : true,
      link: '/dashboard/clients',
    });
  }

  items.sort((a, b) => String(b.at || '').localeCompare(String(a.at || '')));
  const trimmed = items.slice(0, 40);
  const unreadCount = trimmed.reduce(
    (n, it) => n + (it.unread ? it.count || 1 : 0),
    0
  );
  return { items: trimmed, unreadCount };
};
